import { Alert, Button, CircularProgress, Divider, Switch, Typography } from '@mui/material';
import {
    CHANNEL_EMAIL,
    CHANNEL_SLACK,
    CHANNEL_TEAMS,
    connectSlackOAuth,
    connectTeamsOAuth,
    fetchDeliveryPrefs,
    fetchSlackInstallation,
    fetchTeamsInstallation,
    updateDeliveryPref,
} from '/b2b/notifications/notificationDeliveryServices';
import React, { useCallback, useEffect, useState } from 'react';

import Locale from '/b2b/common/components/Locale';
import Portlet from '/b2b/common/components/Portlet';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import styles from './NotificationDeliveryPreferences.styles';

const useStyles = makeStyles(styles);

// Mirrors the server's defaults in `cust-user-handler.js`
const CHANNEL_DEFAULTS = {
    [CHANNEL_EMAIL]: true,
    [CHANNEL_SLACK]: false,
    [CHANNEL_TEAMS]: false,
};

const channelEnabled = (channels, channel) => {
    const row = channels?.find(c => c.channel === channel);
    if (row) {
        return row.enabled;
    }
    return CHANNEL_DEFAULTS[channel] ?? false;
};

/**
 * Presentational connect + toggle row shared by the chat channels (Slack and
 * Teams). Both channels expose the same locale key structure under
 * `localePrefix` and the same install/resolve lifecycle, so the surface is
 * identical apart from the strings and handlers passed in.
 */
const ChatChannelRow = ({
    classes,
    localePrefix,
    error,
    installed,
    resolved,
    teamName,
    canManageWorkspace,
    onConnect,
    connectBusy,
    enabled,
    onToggle,
    saving,
    toggleDisabled,
    ariaLabel,
}) => (
    <div className={classes.channelRow}>
        <div className={classes.channelText}>
            <Typography variant="subtitle2" className={classes.channelLabel}>
                <Locale path={`${localePrefix}.label`} />
            </Typography>
            {error && (
                <Typography variant="body2" className={classes.helperText}>
                    <Locale path={`${localePrefix}.unavailable`} />
                </Typography>
            )}
            {!error && !installed && (
                <Typography variant="body2" className={classes.helperText}>
                    <Locale
                        path={
                            canManageWorkspace
                                ? `${localePrefix}.notInstalled.admin`
                                : `${localePrefix}.notInstalled.user`
                        }
                    />
                </Typography>
            )}
            {!error && installed && !resolved && (
                <Typography variant="body2" className={classes.helperText}>
                    <Locale path={`${localePrefix}.notResolved`} />
                </Typography>
            )}
            {!error && installed && resolved && (
                <Typography variant="body2" className={classes.helperText}>
                    <Locale path={`${localePrefix}.connected`} options={{ teamName }} />
                </Typography>
            )}
        </div>
        <div className={classes.controls}>
            {!installed && canManageWorkspace && (
                <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    onClick={onConnect}
                    disabled={connectBusy}
                >
                    <Locale path={`${localePrefix}.connect`} />
                </Button>
            )}
            <Switch
                checked={enabled}
                color="primary"
                className={classes.switch}
                onChange={e => onToggle(e.target.checked)}
                disabled={toggleDisabled}
                inputProps={{ 'aria-label': ariaLabel }}
            />
            {saving && <CircularProgress size={16} />}
        </div>
    </div>
);

ChatChannelRow.propTypes = {
    ariaLabel: PropTypes.string.isRequired,
    canManageWorkspace: PropTypes.bool,
    classes: PropTypes.object.isRequired,
    connectBusy: PropTypes.bool,
    enabled: PropTypes.bool,
    error: PropTypes.bool,
    installed: PropTypes.bool,
    localePrefix: PropTypes.string.isRequired,
    onConnect: PropTypes.func.isRequired,
    onToggle: PropTypes.func.isRequired,
    resolved: PropTypes.bool,
    saving: PropTypes.bool,
    teamName: PropTypes.string,
    toggleDisabled: PropTypes.bool,
};

const NotificationDeliveryPreferences = ({
    canManageWorkspace,
    slackIntegrationEnabled,
    teamsIntegrationEnabled,
}) => {
    const classes = useStyles();
    const anyIntegrationEnabled = slackIntegrationEnabled || teamsIntegrationEnabled;

    const [loading, setLoading] = useState(true);
    // Channels currently mid-flight. Tracked per-channel
    const [savingChannels, setSavingChannels] = useState(() => new Set());
    const [installBusy, setInstallBusy] = useState(false);
    const [teamsInstallBusy, setTeamsInstallBusy] = useState(false);
    const [prefsError, setPrefsError] = useState(null);
    const [slackError, setSlackError] = useState(null);
    const [teamsError, setTeamsError] = useState(null);
    const [installation, setInstallation] = useState(null);
    const [teamsInstallation, setTeamsInstallation] = useState(null);
    const [channels, setChannels] = useState([]);

    const isSaving = useCallback(channel => savingChannels.has(channel), [savingChannels]);
    const markSaving = useCallback((channel, on) => {
        setSavingChannels(prev => {
            const next = new Set(prev);
            if (on) {
                next.add(channel);
            } else {
                next.delete(channel);
            }
            return next;
        });
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        setPrefsError(null);
        setSlackError(null);
        setTeamsError(null);

        // Only fetch a channel's install state when its integration is enabled,
        // so we don't hit /slack or /teams for orgs that can't use them. The
        // placeholders keep the destructured indices stable.
        const [prefsResult, slackResult, teamsResult] = await Promise.allSettled([
            fetchDeliveryPrefs(),
            slackIntegrationEnabled ? fetchSlackInstallation() : Promise.resolve(null),
            teamsIntegrationEnabled ? fetchTeamsInstallation() : Promise.resolve(null),
        ]);

        if (prefsResult.status === 'fulfilled') {
            setChannels(prefsResult.value?.channels ?? []);
        } else {
            setPrefsError(prefsResult.reason);
        }

        if (slackIntegrationEnabled) {
            if (slackResult.status === 'fulfilled') {
                setInstallation(slackResult.value);
            } else {
                setSlackError(slackResult.reason);
                setInstallation(null);
            }
        }

        if (teamsIntegrationEnabled) {
            if (teamsResult.status === 'fulfilled') {
                setTeamsInstallation(teamsResult.value);
            } else {
                setTeamsError(teamsResult.reason);
                setTeamsInstallation(null);
            }
        }

        setLoading(false);
    }, [slackIntegrationEnabled, teamsIntegrationEnabled]);

    useEffect(() => {
        if (anyIntegrationEnabled) {
            refresh();
        }
    }, [anyIntegrationEnabled, refresh]);

    const handleToggle = useCallback(
        async (channel, enabled) => {
            markSaving(channel, true);
            setPrefsError(null);
            try {
                await updateDeliveryPref({ channel, enabled });
                setChannels(prev => {
                    const filtered = prev.filter(c => c.channel !== channel);
                    return [...filtered, { channel, enabled }];
                });
            } catch (err) {
                setPrefsError(err);
            } finally {
                markSaving(channel, false);
            }
        },
        [markSaving]
    );

    const handleConnectSlack = useCallback(async () => {
        setInstallBusy(true);
        setSlackError(null);
        try {
            // Opens Slack OAuth popup, waits for /connect to postMessage back
            // with {code, state}, exchanges the code server-side. On success the
            // install row exists in b2b.slack_installations.
            await connectSlackOAuth();
            await refresh();
        } catch (err) {
            setSlackError(err);
        } finally {
            setInstallBusy(false);
        }
    }, [refresh]);

    const handleConnectTeams = useCallback(async () => {
        setTeamsInstallBusy(true);
        setTeamsError(null);
        try {
            // Opens the Microsoft Teams admin-consent popup, waits for /connect
            // to postMessage back with {code, state}, exchanges it server-side.
            // On success the install row exists in b2b.teams_installations.
            await connectTeamsOAuth();
            await refresh();
        } catch (err) {
            setTeamsError(err);
        } finally {
            setTeamsInstallBusy(false);
        }
    }, [refresh]);

    // Nothing to show unless at least one chat integration is enabled.
    if (!anyIntegrationEnabled) {
        return null;
    }

    const emailEnabled = channelEnabled(channels, CHANNEL_EMAIL);
    const slackEnabled = channelEnabled(channels, CHANNEL_SLACK);
    const teamsEnabled = channelEnabled(channels, CHANNEL_TEAMS);
    const slackInstalled = !!installation?.installed;
    const slackResolved = !!installation?.currentUserResolved;
    const teamsInstalled = !!teamsInstallation?.installed;
    const teamsResolved = !!teamsInstallation?.currentUserResolved;
    // If the initial GET failed we don't know the true server state — block
    // updates so the user can't PUT against a stale optimistic value.
    const prefsLocked = !!prefsError;
    const emailToggleDisabled = prefsLocked || isSaving(CHANNEL_EMAIL);
    const slackToggleDisabled =
        prefsLocked || !slackInstalled || !slackResolved || isSaving(CHANNEL_SLACK);
    const teamsToggleDisabled =
        prefsLocked || !teamsInstalled || !teamsResolved || isSaving(CHANNEL_TEAMS);

    return (
        <Portlet className={classes.card} loading={loading}>
            <Typography variant="h3" className={classes.cardTitle}>
                <Locale path="notifications.delivery.title" />
            </Typography>
            <Typography variant="body2" className={classes.cardDescription}>
                <Locale path="notifications.delivery.description" />
            </Typography>
            <Divider className={classes.cardDivider} />
            {!loading && (
                <>
                    {prefsError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <Locale path="notifications.delivery.prefsError" />
                        </Alert>
                    )}
                    <div className={classes.rows}>
                        {/* Email channel — always available, defaults to enabled. */}
                        <div className={classes.channelRow}>
                            <div className={classes.channelText}>
                                <Typography variant="subtitle2" className={classes.channelLabel}>
                                    <Locale path="notifications.delivery.email.label" />
                                </Typography>
                                <Typography variant="body2" className={classes.helperText}>
                                    <Locale path="notifications.delivery.email.helper" />
                                </Typography>
                            </div>
                            <div className={classes.controls}>
                                <Switch
                                    checked={emailEnabled}
                                    color="primary"
                                    className={classes.switch}
                                    onChange={e => handleToggle(CHANNEL_EMAIL, e.target.checked)}
                                    disabled={emailToggleDisabled}
                                    inputProps={{ 'aria-label': 'email-notifications' }}
                                />
                                {isSaving(CHANNEL_EMAIL) && <CircularProgress size={16} />}
                            </div>
                        </div>

                        {/* Slack channel — gated behind the chatIntegration plan entitlement. */}
                        {slackIntegrationEnabled && (
                            <ChatChannelRow
                                classes={classes}
                                localePrefix="notifications.delivery.slack"
                                error={!!slackError}
                                installed={slackInstalled}
                                resolved={slackResolved}
                                teamName={installation?.teamName || 'Slack'}
                                canManageWorkspace={canManageWorkspace}
                                onConnect={handleConnectSlack}
                                connectBusy={installBusy}
                                enabled={slackEnabled}
                                onToggle={checked => handleToggle(CHANNEL_SLACK, checked)}
                                saving={isSaving(CHANNEL_SLACK)}
                                toggleDisabled={slackToggleDisabled}
                                ariaLabel="slack-notifications"
                            />
                        )}

                        {/* Microsoft Teams channel — chatIntegration entitlement + EnableTeamsIntegration. */}
                        {teamsIntegrationEnabled && (
                            <ChatChannelRow
                                classes={classes}
                                localePrefix="notifications.delivery.teams"
                                error={!!teamsError}
                                installed={teamsInstalled}
                                resolved={teamsResolved}
                                teamName={teamsInstallation?.teamName || 'Microsoft Teams'}
                                canManageWorkspace={canManageWorkspace}
                                onConnect={handleConnectTeams}
                                connectBusy={teamsInstallBusy}
                                enabled={teamsEnabled}
                                onToggle={checked => handleToggle(CHANNEL_TEAMS, checked)}
                                saving={isSaving(CHANNEL_TEAMS)}
                                toggleDisabled={teamsToggleDisabled}
                                ariaLabel="teams-notifications"
                            />
                        )}
                    </div>
                </>
            )}
        </Portlet>
    );
};

NotificationDeliveryPreferences.propTypes = {
    /** When true, render Slack / Teams Connect controls (admins only). */
    canManageWorkspace: PropTypes.bool,
    /** When true, render the Slack delivery row (chatIntegration plan entitlement). */
    slackIntegrationEnabled: PropTypes.bool,
    /** When true, render the Microsoft Teams delivery row (entitlement + EnableTeamsIntegration). */
    teamsIntegrationEnabled: PropTypes.bool,
};

NotificationDeliveryPreferences.defaultProps = {
    canManageWorkspace: false,
    slackIntegrationEnabled: false,
    teamsIntegrationEnabled: false,
};

export default NotificationDeliveryPreferences;
